import React, { useEffect, useMemo, useState } from 'react';
import { useV3Api } from '../api/useV3Api';
import { errorText } from '../api/errorText';
import PageHeader from '../components/PageHeader';
import PolicyEditor from '../components/policy/PolicyEditor';
import PolicyView from '../components/policy/PolicyView';
import ShowCommand from '../components/ShowCommand';
import { exceptionState, subjectOf, summaryOf } from '../policy/policyModel';
import { GLOBAL, useV3Account } from '../useV3Account';

const ROUTE = 'v3/policies';
const MAX_CHIPS = 3;
const COLUMNS = 8;

const whenText = (utc) => (utc ? String(utc).slice(0, 16).replace('T', ' ') : '');
const keyOf = (p) => `${p.account}|${p.id}`;

// The policies of one account, as the evaluators will see them: the global layer (every customer inherits it) and the customer's own,
// which beats it. The kinds and their fields come from the API (GET v3/policykinds), so a new kind needs no change here.
//
// A policy is opened by clicking its row, as a profile is on the V2 software policy page: its values show under the row, as fields to change
// (Save and Cancel under them), and the other rows are hidden while it is open. A policy of the global layer seen under a customer opens
// read only, with a button to go and change it in Global.
export default function PoliciesPage() {
  const { get, del } = useV3Api();
  const { accounts, account, choose, loading: accountsLoading, error: accountsError } = useV3Account({ withGlobal: true });
  const [kinds, setKinds] = useState({ items: [], error: '' });
  const [list, setList] = useState({ loading: true, items: [], error: '' });
  const [reloads, setReloads] = useState(0);
  const [tab, setTab] = useState('');
  const [showGlobals, setShowGlobals] = useState(false);      // the global layer's policies among a customer's own: hidden until asked for
  const [open, setOpen] = useState('');                 // the key of the policy whose row is open ('' for none)
  const [creating, setCreating] = useState(null);       // the kind of a new policy being made, above the list
  const [newKind, setNewKind] = useState('');
  const [confirming, setConfirming] = useState('');
  const [notice, setNotice] = useState('');
  const [problem, setProblem] = useState('');
  const code = account && account.code;

  useEffect(() => {
    let cancelled = false;
    get('v3/policykinds')
      .then((body) => { if (!cancelled) setKinds({ items: (body && body.items) || [], error: '' }); })
      .catch((ex) => { if (!cancelled) setKinds({ items: [], error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [get]);

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;
    setList((l) => ({ ...l, loading: true }));
    get(ROUTE, { account: code })
      .then((body) => { if (!cancelled) setList({ loading: false, items: (body && body.items) || [], error: '' }); })
      .catch((ex) => { if (!cancelled) setList({ loading: false, items: [], error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [get, code, reloads]);

  const specOf = (kind) => kinds.items.find((k) => k.kind === kind);
  const offered = useMemo(() => kinds.items.filter((k) => code !== GLOBAL || k.global), [kinds.items, code]);
  const visible = useMemo(() => (showGlobals || code === GLOBAL ? list.items : list.items.filter((p) => p.scope !== 'global')), [list.items, showGlobals, code]);
  const inTab = visible.filter((p) => !tab || p.kind === tab);
  const shown = open ? inTab.filter((p) => keyOf(p) === open) : inTab;      // an open policy has the list to itself
  const counts = useMemo(() => visible.reduce((acc, p) => ({ ...acc, [p.kind]: (acc[p.kind] || 0) + 1 }), {}), [visible]);
  const reload = () => setReloads((n) => n + 1);

  const clearMessages = () => { setNotice(''); setProblem(''); };

  const changeAccount = (value) => {
    choose(value);
    setOpen('');
    setCreating(null);
    setConfirming('');
    clearMessages();
  };

  const toggle = (p) => {
    setOpen(open === keyOf(p) ? '' : keyOf(p));
    setCreating(null);
    setConfirming('');
    clearMessages();
  };

  const startNew = () => {
    const spec = specOf(newKind || (offered[0] && offered[0].kind));
    if (spec) { setCreating(spec); setOpen(''); clearMessages(); }
  };

  const saved = (stored) => {
    setCreating(null);
    setOpen('');
    setNotice(`Saved ${subjectOf(stored)} (${(specOf(stored.kind) || {}).title || stored.kind}).`);
    reload();
  };

  const remove = async (policy) => {
    setConfirming('');
    setProblem('');
    try {
      await del(ROUTE, { account: policy.account, id: policy.id });
      setNotice(`Removed ${subjectOf(policy)} (${(specOf(policy.kind) || {}).title || policy.kind}).`);
      if (open === keyOf(policy)) setOpen('');
      reload();
    } catch (ex) {
      setProblem(errorText(ex));
    }
  };

  return (
    <>
      <PageHeader
        title="Policies"
        bear="nuri"
        subtitle="What must be true on a customer's machines. The global layer applies to every customer; a customer's own beats it. Click a policy to open it."
        actions={<ShowCommand route={ROUTE} params={{ account: code, kind: tab }} disabled={!code} />}
      />

      {accountsError && <div className="field-error" role="alert">{accountsError}</div>}
      {kinds.error && <div className="field-error" role="alert">{kinds.error}</div>}
      {notice && <div className="notice" role="status">{notice}</div>}
      {problem && <div className="field-error" role="alert">{problem}</div>}

      {creating && (
        <PolicyEditor key={`new-${creating.kind}`} spec={creating} doc={null} account={code} onSaved={saved} onCancel={() => setCreating(null)} />
      )}

      <div className="card">
        <div className="filter-bar toolbar">
          <div className="field inline">
            <label htmlFor="v3-account">Account</label>
            <select id="v3-account" value={code || ''} disabled={accountsLoading || accounts.length === 0} onChange={(e) => changeAccount(e.target.value)}>
              {accounts.map((a) => <option key={a.code} value={a.code}>{a.code === GLOBAL ? a.name : `${a.code} - ${a.name}`}</option>)}
            </select>
          </div>
          <div className="chips" role="group" aria-label="Kind">
            <button type="button" className={`chip${tab === '' ? ' active' : ''}`} aria-pressed={tab === ''} onClick={() => setTab('')}>All ({visible.length})</button>
            {kinds.items.map((k) => (
              <button type="button" key={k.kind} className={`chip${tab === k.kind ? ' active' : ''}`} aria-pressed={tab === k.kind} onClick={() => setTab(k.kind)}>
                {k.title} ({counts[k.kind] || 0})
              </button>
            ))}
          </div>
          {code !== GLOBAL && <label className="check"><input type="checkbox" checked={showGlobals} onChange={(e) => { setShowGlobals(e.target.checked); setOpen(''); }} />Show global policies</label>}
          <div className="new-policy">
            <select aria-label="Kind of the new policy" value={newKind || (offered[0] ? offered[0].kind : '')} onChange={(e) => setNewKind(e.target.value)} disabled={offered.length === 0}>
              {offered.map((k) => <option key={k.kind} value={k.kind}>{k.title}</option>)}
            </select>
            <button type="button" className="btn btn-primary btn-sm" disabled={offered.length === 0 || !code} onClick={startNew}>New policy</button>
          </div>
        </div>

        {list.loading && <div className="empty">Loading...</div>}
        {list.error && <div className="card-body"><div className="field-error" role="alert">{list.error}</div></div>}
        {!list.loading && !list.error && shown.length === 0 && (
          <div className="empty">{list.items.length === 0 ? 'No policies yet for this account.' : visible.length === 0 ? 'Only global policies apply here, and they are hidden.' : 'No policies of this kind.'}</div>
        )}
        {shown.length > 0 && (
          <div className={`table-wrap${open ? ' has-open' : ''}`}>
            <table className="open-rows" aria-label="Policies">
              <thead>
                <tr>
                  <th style={{ width: 28 }} aria-label="Open" />
                  <th>Layer</th><th>Kind</th><th>Applies to</th><th>Contents</th><th>State</th><th>Changed</th>
                  <th className="row-actions" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => {
                  const spec = specOf(p.kind);
                  const exception = exceptionState(p.exception);
                  const chips = summaryOf(p, spec);
                  const mine = p.account === code;
                  const isOpen = open === keyOf(p);
                  return (
                    <React.Fragment key={keyOf(p)}>
                      <tr className={`row${isOpen ? ' open' : ''}`} data-testid={`policy-${p.id}`} aria-expanded={isOpen} tabIndex={0} onClick={() => toggle(p)}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(p); } }}>
                        <td><span className="caret" aria-hidden="true">&#9656;</span></td>
                        <td><span className={`pill ${p.scope === 'global' ? 'pill-amber' : 'pill-blue'}`}>{p.scope === 'global' ? 'global' : 'customer'}</span></td>
                        <td className="strong">{spec ? spec.title : p.kind}</td>
                        <td>{subjectOf(p)}{p.description && <div className="sub">{p.description}</div>}</td>
                        <td className="contents">
                          {chips.slice(0, MAX_CHIPS).map((c) => <span className="tag" key={c}>{c}</span>)}
                          {chips.length > MAX_CHIPS && <span className="tag">+{chips.length - MAX_CHIPS} more</span>}
                        </td>
                        <td>
                          <span className={`pill ${p.enabled === false ? 'pill-gray' : 'pill-green'}`}>{p.enabled === false ? 'off' : 'on'}</span>
                          {exception.state !== 'none' && <span className={`pill ${exception.state === 'active' ? 'pill-violet' : 'pill-gray'}`}>exception {exception.text}</span>}
                        </td>
                        <td className="sub" title={p.updatedBy ? p.updatedBy.replace(/^user:/, '') : undefined}>
                          {p.updatedBy ? `${p.updatedBy.replace(/^user:/, '').replace(/@.*$/, '')}, ${whenText(p.updatedUtc)}` : ''}
                        </td>
                        <td className="row-actions">
                          {mine && confirming !== keyOf(p) && (
                            <button type="button" className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setConfirming(keyOf(p)); }}>Delete</button>
                          )}
                          {mine && confirming === keyOf(p) && (
                            <>
                              <button type="button" className="btn btn-sm danger" onClick={(e) => { e.stopPropagation(); remove(p); }}>Confirm delete</button>
                              <button type="button" className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); setConfirming(''); }}>Cancel</button>
                            </>
                          )}
                        </td>
                      </tr>
                      {isOpen && spec && (
                        <tr className="detail" data-testid={`detail-${p.id}`}>
                          <td colSpan={COLUMNS}>
                            {mine
                              ? <PolicyEditor key={keyOf(p)} inline spec={spec} doc={p} account={code} onSaved={saved} onCancel={() => setOpen('')} />
                              : <PolicyView spec={spec} doc={p} onEditInGlobal={() => changeAccount(GLOBAL)} />}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
